import React, { useEffect, useState, useCallback } from 'react';
import { AuditLoggers } from '../../../utils/auditLogger';
import { Box, Typography, Paper, CircularProgress, Alert, Button, IconButton, Tooltip, Chip, Select, MenuItem, FormControl, TextField, InputAdornment } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Menu from '@mui/material/Menu';
import UserRolesDrawer from './UserRolesDrawer';
// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';

// Import user rights context
import { useUserRights } from '../../../context/UserRightsContext';

// Import password validation utility
import { validatePassword, getPasswordPolicyText, getPasswordStrength } from '../../../utils/passwordValidation';

// Import date formatting utility
import { formatDate, formatDateTime, initializeFormats } from '../../../utils/global';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const User = () => {
  const apiRef = useGridApiRef();
  const tenantName = (localStorage.getItem('tenant_name') || '').trim();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    manager: '',
    status: 'active'
  });
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [updatingCell, setUpdatingCell] = useState(false);
  const [lastEditedCell, setLastEditedCell] = useState(null);
  // Row actions menu state
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuUserId, setRowMenuUserId] = useState(null);
  // Change password dialog state
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ strength: 0, feedback: [] });
  // Resend password reset dialog state
  const [resendPwdResetDialogOpen, setResendPwdResetDialogOpen] = useState(false);
  const [resendPwdSubmitting, setResendPwdSubmitting] = useState(false);
  // Delete user dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedUserIdToDelete, setSelectedUserIdToDelete] = useState(null);
  const [selectedUserEmailToDelete, setSelectedUserEmailToDelete] = useState('');
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Check if logged in user is a harmonate user
  const isHarmonateUser = isServerHarmonateUser();
  
  // Use the common toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  // Use user rights context
  const { hasPermission, isHarmonateUser: isHarmonateFromContext } = useUserRights();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Use the new API endpoint that returns more detailed user information
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenant/${tenantId}/all-users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error(`Failed to fetch users: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      
      // Process the data to extract additional fields
      const processedUsers = Array.isArray(data) ? data.map(user => {
        // App-level last login (same on every tenant this user can access)
        let lastLoginDate = user.lastLoginAt || user.last_login_at || null;
        if (!lastLoginDate && user.activityLogs && Array.isArray(user.activityLogs)) {
          const loginLogs = user.activityLogs.filter(log => log.action === 'LOGIN');
          if (loginLogs.length > 0) {
            loginLogs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            lastLoginDate = loginLogs[0].created_at;
          }
        }
        
        // Get the role from userRoles
        let role = 'No Role';
        if (user.userRoles && Array.isArray(user.userRoles) && user.userRoles.length > 0) {
          role = user.userRoles[0].role_name;
        }
        
        return {
          ...user,
          lastLoginDate,
          role,
          // Convert status from number to string for compatibility
          status: user.status === 1 ? 'active' : 'inactive',
          harmonate_user: user.harmonate_user === true || user.harmonate_user === 1,
        };
      }) : [];
      
      setUsers(processedUsers);
    } catch (err) {
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    // Check if user is a Harmonate user
    if (isHarmonateUser) {
      showError('Harmonate users cannot add users', 'Access Denied');
      return;
    }
    // Check if user has add permission for User Management function
    if (!hasPermission('Users', 'add')) {
      showError('You do not have permission to add users', 'Access Denied');
      return;
    }
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setForm({ email: '', first_name: '', last_name: '', manager: '', status: 'active' });
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const payload = { 
        ...form, 
        username: form.email, // Automatically set username to same value as email
        status: form.status === 'active' ? 1 : 0,
        tenant_id: tenantId 
      };
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!resp.ok) {
        const errorData = await resp.json();
        const errorMessage = errorData.message || 'Failed to create user';
        
        // Log failed user creation
        await AuditLoggers.userCreated(form.email, false, errorMessage);
        
        // Handle specific duplicate email error
        if (errorMessage.includes('already exists') && errorMessage.includes('email')) {
          showError('This email address is already registered. Please use a different email address.', 'Duplicate Email');
        } else {
          showError(errorMessage, 'User Creation Failed');
        }
        return;
      }
      
      // Log successful user creation
      await AuditLoggers.userCreated(form.email);
      
      handleCloseDialog();
      fetchUsers();
      showSuccess('User created successfully!', 'Success');
    } catch (err) {
      showError(err.message || 'An unexpected error occurred. Please try again.', 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (params) => {
    // Only open drawer if not in edit mode
    if (!editMode) {
      setSelectedUserId(params.row.id);
      setSelectedUserEmail(params.row.email || '');
      setDrawerOpen(true);
    }
  };
  
  // Toggle edit mode
  const toggleEditMode = () => {
    // Check if user is a Harmonate user
    if (isHarmonateUser) {
      showError('Harmonate users cannot edit users', 'Access Denied');
      return;
    }
    // Check if user has edit permission for User Management function
    if (!hasPermission('Users', 'edit')) {
      showError('You do not have permission to edit users', 'Access Denied');
      return;
    }
    
    setEditMode(!editMode);
  };
  
  // Save the edited cell data
  const saveEditedCell = useCallback(async (id, field, value) => {
    if (updatingCell) return;
    
    // Prevent updates for Harmonate users
    if (isHarmonateUser) {
      showError('Harmonate users cannot edit users', 'Access Denied');
      return;
    }
    
    setUpdatingCell(true);
    try {
      const row = users.find(user => user.id === id);
      if (!row) throw new Error('User not found');
      
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      // Only send the edited field in the request body
      const payload = {
        [field]: field === 'status' ? (value === 'active' ? 1 : 0) : value,
        tenant_id: tenantId
      };
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.message || `Failed to update user: ${resp.status} ${resp.statusText}`;
        
        // Log failed user update
        await AuditLoggers.userUpdated(row, field, payload[field]);
        
        throw new Error(errorMessage);
      }
      
      // Log successful user update
      await AuditLoggers.userUpdated(row, field, payload[field]);
      
      // Update was successful
      console.log(`Successfully updated ${field} for user ${id}`);
    } catch (err) {
      console.error('Error updating user:', err);
      alert(err.message);
      
      // Revert the change in the local state
      fetchUsers();
    } finally {
      setUpdatingCell(false);
    }
  }, [updatingCell, users]);
  
  // Handle cell focus out - save the cell if it was edited
  const handleCellFocusOut = useCallback((params) => {
    if (!editMode || !lastEditedCell) return;
    
    const { id, field, value } = lastEditedCell;
    saveEditedCell(id, field, value);
    setLastEditedCell(null);
  }, [editMode, lastEditedCell, saveEditedCell]);
  
  // Process row update when cell editing is done
  const processRowUpdate = (newRow, oldRow) => {
    // Prevent updates for Harmonate users
    if (isHarmonateUser) {
      showError('Harmonate users cannot edit users', 'Access Denied');
      return oldRow;
    }
    
    // Find which field was changed
    const changedField = Object.keys(newRow).find(field => newRow[field] !== oldRow[field]);
    
    if (changedField && editMode) {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (token && tenantId) {
        console.log(`Saving ${changedField} for user ${newRow.id} immediately`);
        
        // Prepare payload with username sync when email changes
        let payload = {
          [changedField]: changedField === 'status' ? (newRow[changedField] === 'active' ? 1 : 0) : newRow[changedField],
          tenant_id: tenantId
        };
        
        // If email was changed, also update username with same value
        if (changedField === 'email') {
          payload.username = newRow.email;
        }
        
        // Make the API call and handle the response
        fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${newRow.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })
        .then(async (resp) => {
          if (resp.ok) {
            console.log(`Successfully saved ${changedField} for user ${newRow.id}`);
            await AuditLoggers.userUpdated(newRow, changedField, payload[changedField]);
            // Update local state after successful API call
            setUsers(prevUsers => prevUsers.map(row => 
              row.id === newRow.id ? { ...row, [changedField]: changedField === 'status' ? (newRow[changedField] === 'active' ? 1 : 0) : newRow[changedField] } : row
            ));
          } else {
            console.error(`Failed to save ${changedField} for user ${newRow.id}: ${resp.status}`);
            // Show error toast
            showError(`Failed to update ${changedField}`, 'Update Failed');
          }
        })
        .catch(err => {
          console.error('Error saving cell:', err);
          showError(`Failed to update ${changedField}`, 'Update Failed');
        });
      }
    }
    
    // Always return the old row to prevent immediate UI update
    return oldRow;
  };

  // Row actions menu handlers
  const openRowMenu = (event, userId) => {
    event.stopPropagation();
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuUserId(userId);
  };

  const closeRowMenu = () => {
    setRowMenuAnchorEl(null);
  };

  // Change password dialog handlers
  const handleOpenChangePassword = () => {
    closeRowMenu();
    // Get the selected user's email
    const selectedUser = users.find(user => user.id === rowMenuUserId);
    setSelectedUserEmail(selectedUser?.email || '');
    setPwdForm({ new_password: '', confirm_password: '' });
    setPwdDialogOpen(true);
  };

  const handleCloseChangePassword = () => {
    setPwdDialogOpen(false);
    setPwdForm({ new_password: '', confirm_password: '' });
    setSelectedUserEmail('');
    setPasswordStrength({ strength: 0, feedback: [] });
  };

  // Resend password reset dialog handlers
  const handleOpenResendPasswordReset = () => {
    closeRowMenu();
    // Get the selected user's email
    const selectedUser = users.find(user => user.id === rowMenuUserId);
    setSelectedUserEmail(selectedUser?.email || '');
    setResendPwdResetDialogOpen(true);
  };

  const handleCloseResendPasswordReset = () => {
    setResendPwdResetDialogOpen(false);
    setSelectedUserEmail('');
  };

  const handleSubmitResendPasswordReset = async () => {
    if (!rowMenuUserId) return;
    
    setResendPwdSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/resend-password-reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: selectedUserEmail,
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to send password reset email';
        
        // Log failed password reset attempt
        await AuditLoggers.passwordResetRequested(selectedUserEmail, false, errorMessage);
        
        throw new Error(errorMessage);
      }
      
      // Log successful password reset
      await AuditLoggers.passwordResetRequested(selectedUserEmail);

      handleCloseResendPasswordReset();
      showSuccess('Password reset email has been sent successfully', 'Success');
    } catch (err) {
      showError(err.message || 'Failed to send password reset email', 'Error');
    } finally {
      setResendPwdSubmitting(false);
    }
  };

  const handlePwdFormChange = (e) => {
    const { name, value } = e.target;
    setPwdForm({ ...pwdForm, [name]: value });
    
    // Update password strength when new password changes
    if (name === 'new_password') {
      const strength = getPasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const handleSubmitChangePassword = async (e) => {
    e.preventDefault();
    if (!rowMenuUserId) return;
    if (!pwdForm.new_password || !pwdForm.confirm_password) {
      showError('Please fill in all password fields.', 'Validation');
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      showError('New password and confirm password do not match.', 'Validation');
      return;
    }

    // Validate password policy
    const passwordValidation = validatePassword(pwdForm.new_password);
    if (!passwordValidation.isValid) {
      showError(`Password validation failed: ${passwordValidation.errors.join(', ')}`, 'Password Policy');
      return;
    }

    setPwdSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${rowMenuUserId}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_password: pwdForm.new_password,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const errorMessage = data.message || 'Failed to change password';
        
        // Log failed password change attempt
        await AuditLoggers.passwordChanged(rowMenuUserId, false, errorMessage);
        
        throw new Error(errorMessage);
      }
      
      const pwdUser = users.find((u) => u.id === rowMenuUserId);
      await AuditLoggers.passwordChanged(pwdUser || selectedUserEmail || rowMenuUserId);

      handleCloseChangePassword();
      showSuccess('Password updated successfully', 'Success');
    } catch (err) {
      showError(err.message || 'Failed to change password', 'Error');
    } finally {
      setPwdSubmitting(false);
    }
  };

  useEffect(() => {
    const tenantId = localStorage.getItem('tenant_id');
    if (tenantId) {
      initializeFormats(tenantId).catch(() => {});
    }
    fetchUsers();
  }, []);

  // Non-Harmonate users must not see Harmonate users on User Management
  const visibleUsers = isHarmonateUser
    ? users
    : users.filter((user) => !user.harmonate_user);

  // Filter users based on search query across multiple fields
  const filteredUsers = visibleUsers.filter((user) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const email = (user.email || '').toLowerCase();
    const firstName = (user.first_name || '').toLowerCase();
    const lastName = (user.last_name || '').toLowerCase();
    const manager = (user.manager || '').toLowerCase();
    
    return email.includes(query) || firstName.includes(query) || lastName.includes(query) || manager.includes(query);
  });

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handle delete user
  const handleOpenDeleteDialog = () => {
    // Check if user is a Harmonate user
    if (isHarmonateUser) {
      showError('Harmonate users cannot delete users', 'Access Denied');
      closeRowMenu();
      return;
    }
    // Check if user has delete permission for User Management function
    if (!hasPermission('Users', 'delete')) {
      showError('You do not have permission to delete users', 'Access Denied');
      closeRowMenu();
      return;
    }
    
    // Use the user ID from the row menu
    if (!rowMenuUserId) {
      showError('User not found', 'Error');
      closeRowMenu();
      return;
    }
    
    // Get the user details
    const userToDelete = users.find(user => user.id === rowMenuUserId);
    if (!userToDelete) {
      showError('User not found', 'Error');
      closeRowMenu();
      return;
    }
    
    // Check if user status is inactive - only inactive users can be deleted
    const userStatus = userToDelete.status;
    const isInactive = userStatus === 'inactive' || userStatus === 0 || userStatus === 'Inactive';
    if (!isInactive) {
      showError('Only inactive users can be deleted. Please deactivate the user first.', 'Cannot Delete Active User');
      closeRowMenu();
      return;
    }
    
    setSelectedUserIdToDelete(rowMenuUserId);
    setSelectedUserEmailToDelete(userToDelete.email || '');
    setDeleteDialogOpen(true);
    closeRowMenu();
  };

  const handleDeleteUser = async () => {
    if (!selectedUserIdToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${selectedUserIdToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.message || `Failed to delete user: ${resp.status} ${resp.statusText}`;
        
        // Log failed user deletion attempt
        await AuditLoggers.userDeleted(selectedUserIdToDelete, selectedUserEmailToDelete, false, errorMessage);
        
        throw new Error(errorMessage);
      }
      
      // Log successful user deletion
      await AuditLoggers.userDeleted(selectedUserIdToDelete, selectedUserEmailToDelete);

      handleCloseDeleteDialog();
      // Clear selected user and close drawer if open
      setSelectedUserId(null);
      setDrawerOpen(false);
      fetchUsers();
      showSuccess('User deleted successfully', 'Success');
    } catch (err) {
      console.error('Error deleting user:', err);
      showError(err.message || 'Failed to delete user', 'Error');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUserIdToDelete(null);
    setSelectedUserEmailToDelete('');
  };

  // Export users to Excel (CSV format)
  const handleExportToExcel = async () => {
    // Check permission before exporting
    if (!isHarmonateUser && !hasPermission('Users', 'export')) {
      showError('You do not have permission to export users', 'Access Denied');
      return;
    }

    try {
      // Prepare CSV headers with new fields
      const headers = ['Email', 'First Name', 'Last Name', 'Manager', 'Status', 'Created At', 'Last Login', 'Role'];
      
      // Prepare CSV rows from filtered users
      const rows = filteredUsers.map(user => [
        user.email || '',
        user.first_name || '',
        user.last_name || '',
        user.manager || '',
        user.status === 'active' ? 'Active' : 'Inactive',
        user.created_at ? formatDate(user.created_at) : '',
        user.lastLoginDate ? formatDateTime(user.lastLoginDate) : 'No Record',
        user.role || 'No Role'
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `user_list_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      await AuditLoggers.userListExported(filteredUsers.length, tenantName);
      showSuccess('User list exported successfully!', 'Export Complete');
    } catch (err) {
      showError('Failed to export user list', 'Export Failed');
      console.error('Export error:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>Loading users...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load users
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={fetchUsers} 
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    { 
      field: 'email', 
      headerName: 'Email', 
      flex: 1,
      editable: editMode && !isHarmonateUser 
    },
    { 
      field: 'first_name', 
      headerName: 'First Name', 
      flex: 1,
      editable: editMode && !isHarmonateUser 
    },
    { 
      field: 'last_name', 
      headerName: 'Last Name', 
      flex: 1,
      editable: editMode && !isHarmonateUser 
    },
    { 
      field: 'manager', 
      headerName: 'Manager', 
      flex: 1,
      editable: editMode && !isHarmonateUser 
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      editable: editMode && !isHarmonateUser,
      type: 'singleSelect',
      valueOptions: ['active', 'inactive'],
      renderCell: (params) => {
        const rawStatus = params.value;
        
        // Handle null, undefined, or non-string values
        let status = 'inactive';
        if (rawStatus && typeof rawStatus === 'string') {
          status = rawStatus;
        }
        
        const isActive = status.toLowerCase() === 'active';
        
        return (
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
            size="small"
            sx={{
              bgcolor: isActive ? 'rgba(2, 107, 29, 0.12)' : 'rgba(239, 63, 63, 0.12)',
              color: isActive ? ht.green : ht.red,
              borderRadius: ht.buttonRadius,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      },
      renderEditCell: (params) => {
        const { id, value, field } = params;
        
        return (
          <FormControl fullWidth size="small">
            <Select
              value={value || 'inactive'}
              onChange={(event) => {
                params.api.setEditCellValue({ id, field, value: event.target.value });
              }}
              MenuProps={harmonateSelectMenuProps}
              sx={{
                borderRadius: ht.fieldRadius,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                  fontSize: '0.875rem'
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: ht.text },
              }}
            >
              <MenuItem value="active" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
              <MenuItem value="inactive" sx={{ fontSize: '0.875rem' }}>Inactive</MenuItem>
            </Select>
          </FormControl>
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 120,
      renderCell: (params) => {
        return formatDate(params.value);
      }
    },
    {
      field: 'lastLoginDate',
      headerName: 'Last Login',
      width: 180,
      renderCell: (params) => {
        return params.value ? formatDateTime(params.value) : 'No Record';
      }
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params) => {
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: ht.secondaryTab,
              color: ht.text,
              borderRadius: ht.buttonRadius,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      }
    },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, mr: 2 }}>
          <Typography variant="h6" sx={harmonatePageTitleSx}>
            Users
          </Typography>
          {tenantName ? (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: ht.white,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={tenantName}
            >
              {tenantName}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Export Button */}
          <Tooltip title={
            !isHarmonateUser && !hasPermission('Users', 'export')
              ? "You do not have permission to export users"
              : "Export user list to CSV"
          }>
            <span>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportToExcel}
                disabled={!isHarmonateUser && !hasPermission('Users', 'export')}
                sx={harmonateUploadOutlinedSx}
              >
                Export User List
              </Button>
            </span>
          </Tooltip>
          {/* Search Bar */}
          <TextField
            placeholder="Search by email, first name, or last name..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{ ...harmonatePageSearchFieldSx, minWidth: 350 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={harmonatePageSearchIconSx} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ color: ht.white }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={
            isHarmonateUser
              ? "Harmonate users cannot edit users"
              : !hasPermission('Users', 'edit')
                ? "You do not have permission to edit users"
                : (editMode ? "Exit Edit Mode" : "Enable Cell Editing")
          }>
            <IconButton 
              onClick={toggleEditMode} 
              disabled={isHarmonateUser || !hasPermission('Users', 'edit')}
              sx={{ 
                ...harmonatePlainIconButtonSx,
                color: editMode ? ht.secondaryTab : ht.white,
                '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.4)' },
              }}
            >
              <EditOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={
            isHarmonateUser
              ? "Harmonate users cannot add users"
              : !hasPermission('Users', 'add')
                ? "You do not have permission to add users"
                : "Add New User"
          }>
            <IconButton 
              onClick={handleOpenDialog} 
              disabled={isHarmonateUser || !hasPermission('Users', 'add')}
              sx={harmonatePlainIconButtonSx}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, mb: 3, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rowHeight={40}
          apiRef={apiRef}
          rows={filteredUsers.map((user, idx) => ({
            id: user.id || idx + 1,
            email: user.email || '',
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            manager: user.manager || '',
            status: user.status,
            created_at: user.created_at || '',
            lastLoginDate: user.lastLoginDate || '',
            role: user.role || 'No Role',
            _actions: user.id || idx + 1,
          }))}
          columns={[
            ...columns,
            {
              field: '_actions',
              headerName: '',
              width: 56,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: (params) => (
                <IconButton size="small" onClick={(e) => openRowMenu(e, params.row.id)} sx={harmonateGridActionIconSx}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              ),
            },
          ]}
          columnResizeMode="onEnd"
          resizable
          autoHeight={false}
          sx={{ 
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
            '& .MuiDataGrid-cell--editing': {
              backgroundColor: `${ht.secondaryTab} !important`,
              boxShadow: 'none !important',
            },
          }}
          editMode="cell"
          processRowUpdate={processRowUpdate}
          onCellFocusOut={handleCellFocusOut}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  No users available
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={fetchUsers}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            )
          }}
        />
      </Paper>
      {/* Row actions menu */}
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: ht.fieldRadius,
            '& .MuiMenuItem-root': { fontSize: '0.875rem', color: ht.text },
          },
        }}
      >
        {/* <MenuItem onClick={handleOpenChangePassword}>Change Password</MenuItem> */}
        <MenuItem onClick={handleOpenResendPasswordReset}>Send Password Reset</MenuItem>
        <MenuItem 
          onClick={handleOpenDeleteDialog}
          disabled={(() => {
            if (!rowMenuUserId) return true;
            const user = users.find(u => u.id === rowMenuUserId);
            if (!user) return true;
            const userStatus = user.status;
            const isInactive = userStatus === 'inactive' || userStatus === 0 || userStatus === 'Inactive';
            return !isInactive;
          })()}
          sx={{ color: `${ht.red} !important` }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Change Password Dialog */}
      <Dialog open={pwdDialogOpen} onClose={handleCloseChangePassword} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Change Password</DialogTitle>
        <form onSubmit={handleSubmitChangePassword}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            {selectedUserEmail && (
              <Typography variant="body2" sx={{ mb: 1, color: ht.text, fontSize: '0.875rem' }}>
                Changing password for: <strong>{selectedUserEmail}</strong>
              </Typography>
            )}
            <TextField
              label="New Password"
              name="new_password"
              type="password"
              value={pwdForm.new_password}
              onChange={handlePwdFormChange}
              required
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
            {pwdForm.new_password && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: ht.text }}>
                  Password Strength: {Math.round(passwordStrength.strength * 100)}%
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  {[1, 2, 3].map((level) => (
                    <Box
                      key={level}
                      sx={{
                        height: 4,
                        flex: 1,
                        bgcolor: passwordStrength.strength >= level / 3 ? 
                          (passwordStrength.strength >= 0.7 ? ht.green : 
                           passwordStrength.strength >= 0.4 ? ht.blue : ht.red) : ht.line,
                        borderRadius: 1
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: ht.red }}>
                  Requirements: {passwordStrength.feedback.join(', ')}
                </Typography>
              </Box>
            )}
            <TextField
              label="Confirm New Password"
              name="confirm_password"
              type="password"
              value={pwdForm.confirm_password}
              onChange={handlePwdFormChange}
              required
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseChangePassword} disabled={pwdSubmitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pwdSubmitting} sx={harmonateProceedButtonSx}>
              {pwdSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <UserRolesDrawer 
        open={drawerOpen} 
        onClose={() => setDrawerOpen(false)} 
        userId={selectedUserId} 
        username={selectedUserEmail} 
      />
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add User</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField label="Email" name="email" value={form.email} onChange={handleFormChange} required fullWidth size="small" InputLabelProps={harmonateInputLabelProps} sx={harmonateTextFieldSx} />
            <TextField label="First Name" name="first_name" value={form.first_name} onChange={handleFormChange} required fullWidth size="small" InputLabelProps={harmonateInputLabelProps} sx={harmonateTextFieldSx} />
            <TextField label="Last Name" name="last_name" value={form.last_name} onChange={handleFormChange} required fullWidth size="small" InputLabelProps={harmonateInputLabelProps} sx={harmonateTextFieldSx} />
            <TextField label="Manager" name="manager" value={form.manager} onChange={handleFormChange} fullWidth size="small" InputLabelProps={harmonateInputLabelProps} sx={harmonateTextFieldSx} />
            <TextField
              select
              label="Status"
              name="status"
              value={form.status}
              onChange={handleFormChange}
              fullWidth
              size="small"
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Resend Password Reset Confirmation Dialog */}
      <Dialog open={resendPwdResetDialogOpen} onClose={handleCloseResendPasswordReset} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Send Password Reset</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {selectedUserEmail && (
            <Typography variant="body2" sx={{ mb: 1, color: ht.text, fontSize: '0.875rem' }}>
              Send password reset email to: <strong>{selectedUserEmail}</strong>
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: ht.fadedText, fontSize: '0.875rem' }}>
            This will send a password reset email to the user's email address. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseResendPasswordReset} disabled={resendPwdSubmitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button 
            onClick={handleSubmitResendPasswordReset} 
            variant="contained" 
            disabled={resendPwdSubmitting} 
            sx={harmonateProceedButtonSx}
          >
            {resendPwdSubmitting ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Delete User</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {selectedUserEmailToDelete && (
            <Typography variant="body2" sx={{ mb: 1, color: ht.text, fontSize: '0.875rem' }}>
              Are you sure you want to delete user: <strong>{selectedUserEmailToDelete}</strong>?
            </Typography>
          )}
          <Typography variant="body2" sx={{ fontWeight: 500, color: ht.red, fontSize: '0.875rem' }}>
            This action cannot be undone. All user data will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button 
            onClick={handleDeleteUser} 
            variant="contained" 
            disabled={deleting} 
            sx={harmonateAbortButtonSx}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Common Toast Component */}
      <Toast 
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </Box>
  );
};

export default User;